// The mark of a chat (a row of the Agents list): the chat's bubble, and when
// the chat belongs to a Case, an Action or is the secretary, that one's icon
// small at the bubble's bottom right. What goes on in the chat (working, a
// question, waiting for its turn, delegated, unread) sits small at its top
// left. The bubble's line is cut out in a circle under each small icon (not
// painted over with a ground color), so the mark sits well on any ground: the
// list, a hovered row, the selected row.
import type { AgentChatDTO } from "@trameria/core/agent-chat-shared";
import { m } from "@trameria/i18n";
import { Icon, type IconKind, QUESTION_DOT, QUESTION_HOOK } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import type { ChatState } from "./list-rows";

/** What a chat is tied to, as the icon at the bubble's corner (null: nothing). */
export function chatBadge(
  chat: Pick<AgentChatDTO, "role" | "caseId" | "caseStepId">,
): IconKind | null {
  if (chat.role === "secretary") return "person";
  if (chat.caseId !== null && chat.caseStepId !== null) return "action";
  if (chat.caseId !== null) return "cases";
  return null;
}

export const STATE_LABEL: Record<ChatState, () => string> = {
  asking: () => m.agents_row_asking(),
  queued: () => m.agents_row_queued(),
  working: () => m.agents_row_working(),
  delegating: () => m.agents_row_delegating(),
  unread: () => m.agents_row_unread(),
};

/** Working and a question in the accent; waiting for its turn and delegated quiet. */
const stateTone = (state: ChatState): string =>
  state === "delegating" || state === "queued" ? "text-faint" : "text-accent";

/** Working: an arc turning on a faint ring (around the whole 16 grid). */
function WorkingRing() {
  return (
    <g
      // An SVG element turns about the drawing's origin: about its center here.
      style={{ transformOrigin: "8px 8px" }}
      className="motion-safe:animate-[spin_1.2s_linear_infinite]"
    >
      <circle cx="8" cy="8" r="6" strokeOpacity={0.3} />
      <path d="M8 2a6 6 0 014.24 10.24" />
    </g>
  );
}

/**
 * The drawing of a state: a clock, a question mark, a turning arrow, a dot,
 * and for working an arc turning on a faint ring, the same ring as the clock
 * and the question (one family of round marks, not loose dots). A question
 * asked while the chat goes on working turns its ring as working does (the
 * person 2026-10-29), the mark itself still the question.
 */
function StateGlyph({
  state,
  size,
  stroke = 1.25,
  running = false,
}: {
  state: ChatState;
  size: number;
  /** The line width on the 16 grid (drawn small, a touch bolder to read). */
  stroke?: number;
  /** The chat's agent is at work (asking turns its ring then). */
  running?: boolean;
}) {
  if (state === "queued")
    return <Icon kind="clock" size={size} stroke={stroke} />;
  if (state === "asking" && !running)
    return <Icon kind="question" size={size} stroke={stroke} />;
  if (state === "delegating")
    return <Icon kind="child" size={size} stroke={stroke} />;
  if (state === "unread")
    return (
      <span
        className="rounded-full bg-current"
        style={{ width: size * 0.5, height: size * 0.5 }}
      />
    );
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      aria-hidden="true"
      className="pointer-events-none flex-none"
    >
      <WorkingRing />
      {state === "asking" && (
        <>
          <path d={QUESTION_HOOK} />
          <circle {...QUESTION_DOT} fill="currentColor" stroke="none" />
        </>
      )}
    </svg>
  );
}

// The holes in the bubble's line, in px of its 16 px box: under the state at
// the top left, under the tie at the bottom right.
const STATE_HOLE =
  "radial-gradient(circle at -0.5px 0px,transparent 5.5px,black 6px)";
const BADGE_HOLE =
  "radial-gradient(circle at 15.5px 14.5px,transparent 7px,black 7.5px)";

export function ChatMark({
  badge,
  state = null,
  running = false,
  tone = "text-faint",
  tip = true,
}: {
  badge: IconKind | null;
  state?: ChatState | null;
  /** The chat's agent is at work (a question then turns its ring). */
  running?: boolean;
  /** The bubble's color (a link's own color when the mark is a link's icon). */
  tone?: string;
  /** The state's own tooltip; off inside a control that names it in its own. */
  tip?: boolean;
}) {
  const holes = [state && STATE_HOLE, badge && BADGE_HOLE].filter(Boolean);
  const label = state && STATE_LABEL[state]();
  const stateClass =
    state &&
    `absolute -top-[5px] -left-[5.5px] flex h-[10px] w-[10px] items-center justify-center ${stateTone(state)}`;
  const glyph = state && (
    <StateGlyph state={state} size={10} stroke={1.5} running={running} />
  );
  return (
    <span className={`relative flex flex-none ${tone}`}>
      <span
        className="flex"
        style={
          holes.length > 0
            ? { maskImage: holes.join(","), maskComposite: "intersect" }
            : undefined
        }
      >
        <Icon kind="agentChat" stroke={1.2} />
      </span>
      {state &&
        label &&
        (tip ? (
          <Tooltip text={label}>
            <span
              role="img"
              aria-label={label}
              data-chat-state={state}
              className={stateClass || ""}
            >
              {glyph}
            </span>
          </Tooltip>
        ) : (
          <span
            aria-hidden="true"
            data-chat-state={state}
            className={stateClass || ""}
          >
            {glyph}
          </span>
        ))}
      {badge !== null && (
        <span className="absolute -right-[6px] -bottom-[5px] flex p-px">
          <Icon kind={badge} size={11} />
        </span>
      )}
    </span>
  );
}
